export function createOrderLink(runtime, config) {
  const key = 'pg_ads_order_link_v2';
  const revokeKey = 'pg_ads_order_link_revoke_v2';
  const cartCleanupKey = 'pg_ads_order_link_cart_cleanup_v2';
  const attribute = '__pg_ads_v2__';
  const day = 86400000;
  let active = null;
  let generation = 0;
  let cartQueue = Promise.resolve();
  let lastCartValue = null;
  let stopped = false;
  let productValue = '';

  function syncProducts() {
    const value = active && validate(active) && permitted() && !stopped ? productValue : '';
    try { runtime.productProperty?.(value); } catch {}
  }

  function permitted() {
    try {
      const privacy = runtime.privacy();
      return privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true &&
        privacy.saleOfDataAllowed() === true;
    } catch { return false; }
  }

  function read(storageKey) {
    try { return JSON.parse(runtime.storage.getItem(storageKey) || 'null'); }
    catch { return null; }
  }

  function write(storageKey, value) {
    try {
      if (value === null) runtime.storage.removeItem(storageKey);
      else runtime.storage.setItem(storageKey, JSON.stringify(value));
      return true;
    } catch { return false; }
  }

  function safeToken(value) { return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value); }

  function validate(marker) {
    const now = runtime.now();
    return marker?.v === 2 && safeToken(marker.token) && config.campaignIds.includes(marker.campaign_id) &&
      typeof marker.oppref === 'string' && marker.oppref.length > 0 && marker.oppref.length <= 4096 &&
      !/[\x00-\x20\x7f{}]/.test(marker.oppref) &&
      (config.sandbox || !/^(?:fixture_|pgservice_test_|test_)/i.test(marker.oppref)) &&
      Number.isSafeInteger(marker.click_timestamp_ms) && marker.click_timestamp_ms <= now &&
      marker.click_timestamp_ms > now - config.windowDays * day;
  }

  function landing() {
    const url = new URL(runtime.href());
    const refs = url.searchParams.getAll('oppref');
    const campaigns = url.searchParams.getAll('utm_campaign');
    if (refs.length !== 1 || campaigns.length !== 1) return null;
    const candidate = {v: 2, token: runtime.token(), oppref: refs[0], campaign_id: campaigns[0],
      click_timestamp_ms: runtime.now()};
    for (const {parameter, target, pattern} of [
      {parameter: 'utm_term', target: 'ad_group_id', pattern: /^adgrp_[a-z0-9]+$/},
      {parameter: 'utm_content', target: 'ad_id', pattern: /^ad_[a-z0-9]+$/}
    ]) {
      const values = url.searchParams.getAll(parameter);
      if (values.length === 1 && pattern.test(values[0])) candidate[target] = values[0];
    }
    return validate(candidate) ? candidate : null;
  }

  async function cloud(path, payload) {
    const response = await runtime.fetch(config.endpoint + path, {method: 'POST',
      headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload),
      credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true,
      signal: runtime.timeout(3000)});
    if (!response.ok) throw new Error('cloud_unconfirmed');
    const value = await response.json();
    return value.code;
  }

  function updateCart(value, version) {
    cartQueue = cartQueue.catch(() => {}).then(async () => {
      if (value && (version !== generation || !permitted() || stopped)) return;
      if (value === lastCartValue) return;
      const response = await runtime.fetch(config.cartRoot + 'cart/update.js', {method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({attributes: {[attribute]: value}}), credentials: 'same-origin',
        keepalive: true, signal: runtime.timeout(3000)});
      if (!response.ok) throw new Error('cart_unconfirmed');
      lastCartValue = value;
      if (value && (version !== generation || !permitted() || stopped)) {
        write(cartCleanupKey, true);
        updateCart('', generation).then(() => write(cartCleanupKey, null)).catch(() => {});
      }
    });
    return cartQueue;
  }

  async function drainRevocations() {
    if (read(cartCleanupKey) === true) {
      try { await updateCart('', generation); write(cartCleanupKey, null); } catch {}
    }
    const pending = read(revokeKey);
    if (!Array.isArray(pending)) return;
    const valid = pending.filter(safeToken);
    const unresolved = valid.slice(32);
    for (const token of valid.slice(0, 32)) {
      try {
        if (await cloud('/consent/revoke', {v: 2, token}) !== 'revoked') unresolved.push(token);
      } catch { unresolved.push(token); }
    }
    const current = read(revokeKey);
    const newer = Array.isArray(current) ? current.filter(token => !pending.includes(token)) : [];
    write(revokeKey, [...new Set([...unresolved, ...newer])].length ? [...new Set([...unresolved, ...newer])] : null);
  }

  async function revoke() {
    generation++;
    const prior = active || read(key);
    active = null;
    productValue = '';
    syncProducts();
    write(key, null);
    if (safeToken(prior?.token)) {
      const pending = read(revokeKey);
      write(revokeKey, [...new Set([...(Array.isArray(pending) ? pending : []), prior.token])]);
    }
    if (prior || lastCartValue) write(cartCleanupKey, true);
    await drainRevocations();
  }

  async function refresh() {
    if (stopped) return;
    if (!permitted()) { await revoke(); return; }
    await drainRevocations();
    const version = ++generation;
    const stored = read(key);
    const incoming = landing();
    const marker = incoming && (incoming.oppref !== stored?.oppref || incoming.campaign_id !== stored?.campaign_id)
      ? incoming : validate(stored) ? stored : incoming;
    if (!marker) {
      if (stored) await revoke();
      return;
    }
    if (stored?.token && stored.token !== marker.token) {
      await revoke();
      if (!permitted() || stopped) return;
      return refresh();
    }
    active = marker;
    if (!write(key, marker)) { active = null; return; }
    try {
      const code = await cloud('/consent/grant', {...marker,
        consent: {analytics: true, marketing: true, sale_of_data: true}});
      if (code !== 'granted' || version !== generation || !permitted() || stopped) return;
      productValue = JSON.stringify({v: 2, grant_token: marker.token});
      syncProducts();
      await updateCart(productValue, version);
    } catch {
      // Tracking failure never blocks a customer's commerce flow.
    }
  }

  return {refresh, revoke, retryCleanup: drainRevocations, syncProducts,
    stop: async () => {stopped = true; await revoke();},
    status: () => ({allowed: permitted(), markerPresent: !!active, cartAcknowledged: !!lastCartValue,
      cleanupPending: read(cartCleanupKey) === true ||
        (Array.isArray(read(revokeKey)) && read(revokeKey).length > 0)})};
}

export function syncProductProperties(document, value) {
  for (const form of document.querySelectorAll('form[action$="/cart/add"], form[action$="/cart/add.js"]')) {
    const existing = [...form.querySelectorAll('input[name="properties[__pg_ads_v2__]"]')];
    if (!value) {
      for (const input of existing) input.remove();
      continue;
    }
    const input = existing.shift() || document.createElement('input');
    for (const duplicate of existing) duplicate.remove();
    input.type = 'hidden';
    input.name = 'properties[__pg_ads_v2__]';
    input.value = value;
    if (!input.parentNode) form.appendChild(input);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const element = document.getElementById('pg-ads-order-link-config');
  if (element) {
    let config;
    try { config = JSON.parse(element.textContent); } catch {}
    if (config?.enabled === true && Array.isArray(config.campaignIds) &&
        [7, 14, 30].includes(config.windowDays) &&
        /^https:\/\/pgservice-ads-tracking-(sandbox|live)\.throbbing-math-9ca8\.workers\.dev$/.test(config.endpoint) &&
        /^\/[a-zA-Z0-9/_-]*$/.test(config.cartRoot)) {
      let storage;
      try { storage = window.localStorage; } catch {}
      const link = createOrderLink({storage, now: () => Date.now(),
        href: () => window.location.href, privacy: () => window.Shopify?.customerPrivacy,
        fetch: window.fetch.bind(window), timeout: ms => AbortSignal.timeout(ms),
        productProperty: value => syncProductProperties(document, value),
        token: () => Array.from(crypto.getRandomValues(new Uint8Array(32)),
          value => value.toString(16).padStart(2, '0')).join('')}, config);
      const refresh = () => {link.refresh().catch(() => {});};
      const productForms = new MutationObserver(() => link.syncProducts());
      productForms.observe(document.body, {childList: true, subtree: true});
      document.addEventListener('visitorConsentCollected', refresh);
      window.addEventListener('storage', event => {
        if (['pg_ads_order_link_v2', 'pg_ads_order_link_revoke_v2', 'pg_ads_order_link_cart_cleanup_v2'].includes(event.key)) refresh();
      });
      window.addEventListener('online', refresh);
      window.addEventListener('pageshow', refresh);
      if (window.Shopify?.customerPrivacy) refresh();
      else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures(
        [{name: 'consent-tracking-api', version: '0.1'}], error => {if (!error) refresh();});
    }
  }
}

